import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  AddBeneficiariesBoxStyled,
  BeneficiaryDependentDataWrapper,
} from '../../AddYourBeneficiariesStyled';
// import { BeneficiaryAllocationDropdown } from '../BeneficiaryAllocationDropdown/BeneficiaryAllocationDropdown';
import { BeneficiaryDataCard } from '../BeneficiaryDataCard';
import {
  PrimaryBeneficiaryPlanCardDetails,
  SecondaryBeneficiaryPlanCardDetails,
} from './BeneficiariesHeaders';
import { BeneficiariesDto } from 'enrollment/queries/useEnrollmentAddBeneficiaryQuery/types';
import { AddBeneficiariesDrawer } from '../AddBeneficiariesDrawer';
import { BeneficiaryTotalAllocation } from '../BeneficiaryTotalAllocation';
import { selectNewBeneficiary } from '../../services';
import { getTotalAllocation } from './utils';

interface BeneficiariesProps {
  orgBenefitId: number;
  isPrimary: boolean;
  setDisabled: any;
  emptyPrimaryBeneficiaries: boolean;
  coverageFields: any;
  allBeneficiaries: BeneficiariesDto[];
  metadata: any;
  isBeneficiaryLoading?: boolean;
  boldHeader?: boolean;
  hideAddToAllBenefits?: boolean;
  autoSave?: boolean;
  setTotalAllocation?: any;
  coverageLineId?: number;
  numCoverageLines?: number;
}

export const Beneficiaries = ({
  orgBenefitId,
  isPrimary,
  setDisabled,
  emptyPrimaryBeneficiaries,
  coverageFields,
  allBeneficiaries,
  metadata,
  isBeneficiaryLoading = false,
  boldHeader,
  hideAddToAllBenefits,
  autoSave,
  setTotalAllocation,
  coverageLineId,
  numCoverageLines = 1,
}: BeneficiariesProps) => {
  const benefitPlansState = useSelector(selectNewBeneficiary).beneficiaryData;

  const benefitData = benefitPlansState?.find((benefitPlan) => {
    return benefitPlan.orgBenefitId === orgBenefitId;
  });
  const beneficiaries = useMemo(
    () => benefitData?.beneficiaries,
    [benefitData?.beneficiaries]
  );
  const beneficiariesFiltered =
    beneficiaries?.filter((beneficiary) => {
      if (coverageLineId) {
        return (
          beneficiary.coverage_line_id === coverageLineId &&
          beneficiary.primary_beneficiary === isPrimary
        );
      }
      return beneficiary.primary_beneficiary === isPrimary;
    }) || [];

  const totalAllocation = getTotalAllocation(beneficiariesFiltered);

  const zeroAllocation = isPrimary
    ? benefitData?.primaryZeroAllocation
    : benefitData?.secondaryZeroAllocation;

  const emptyAllocation = isPrimary
    ? benefitData?.primaryEmptyAllocation
    : benefitData?.secondaryEmptyAllocation;

  const [dropdownAllocationValue, setDropdownAllocationValue] =
    useState('percentage');

  const handleDropdownAllocation = (event: any) => {
    setDropdownAllocationValue(event?.target?.value);
  };

  useEffect(() => {
    //select-field
    const element = document.getElementById('allocation-select-field');
    element?.addEventListener('change', (event) => {
      handleDropdownAllocation(event);
    });

    //show selected options text field
    const percentageFieldElement = document.querySelectorAll(
      '.percentage-input-field-container'
    );
    const amountFieldElement = document.querySelectorAll(
      '.amount-input-field-container'
    );
    if (dropdownAllocationValue === 'percentage') {
      percentageFieldElement.forEach((element) => {
        element.classList.remove('d-none');
        element.classList.add('d-block');
      });
      amountFieldElement.forEach((element) => {
        element.classList.remove('d-block');
        element.classList.add('d-none');
      });
    } else if (dropdownAllocationValue === 'amount') {
      percentageFieldElement.forEach((element) => {
        element.classList.remove('d-block');
        element.classList.add('d-none');
      });
      amountFieldElement.forEach((element) => {
        element.classList.remove('d-none');
        element.classList.add('d-block');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (setTotalAllocation) {
      setTotalAllocation(totalAllocation);
    }
  }, [totalAllocation, setTotalAllocation]);

  return (
    <>
      <AddBeneficiariesBoxStyled
        sx={{ paddingTop: coverageLineId ? '8px' : '' }}
      >
        {isPrimary ? (
          <PrimaryBeneficiaryPlanCardDetails boldHeader={boldHeader} />
        ) : (
          <SecondaryBeneficiaryPlanCardDetails boldHeader={boldHeader} />
        )}
        <>
          {/* commenting this code as of now because need to confirm with PO as it may need in future or not */}
          {/* {beneficiaries?.length > 0 && (
            <BeneficiaryAllocationDropdown
              dropdownAllocationValue={dropdownAllocationValue}
              handleDropdownAllocation={handleDropdownAllocation}
            />
          )} */}
          <BeneficiaryDependentDataWrapper
            className={beneficiariesFiltered.length > 0 ? 'mt-4 mt-md-5' : ''}
            data-testid="beneficiaryDependentDataWrapper"
          >
            {beneficiariesFiltered.map((beneficiary, index) => {
              return (
                <BeneficiaryDataCard
                  key={`${orgBenefitId}-${index}`}
                  beneficiaryDetail={beneficiary as BeneficiariesDto}
                  orgBenefitId={orgBenefitId}
                />
              );
            })}
          </BeneficiaryDependentDataWrapper>
        </>
      </AddBeneficiariesBoxStyled>

      <div className="d-flex flex-row flex-md-column-reverse flex-lg-row align-items-center mb-4_5 mt-0 mt-md-3 my-lg-4_5">
        <div className="flex-grow-0 flex-md-grow-1 align-self-md-start mt-md-5 mt-lg-0">
          <AddBeneficiariesDrawer
            key={`${
              isPrimary ? `primary` : `secondary`
            }-drawer-${orgBenefitId}`}
            orgBenefitId={orgBenefitId}
            allocatedBeneficiaries={beneficiaries}
            metadata={metadata}
            allBeneficiaries={allBeneficiaries}
            isLoading={isBeneficiaryLoading}
            isPrimary={isPrimary}
            hideAddToAllBenefits={hideAddToAllBenefits}
            coverageLineId={coverageLineId}
          />
        </div>
        <div className="flex-grow-1 flex-md-grow-1 flex-lg-grow-0 text-right text-md-center text-lg-right">
          <BeneficiaryTotalAllocation
            key={`${
              isPrimary ? `primary` : `secondary`
            }-total-allocation-${orgBenefitId}`}
            dropdownAllocationValue={dropdownAllocationValue}
            orgBenefitId={orgBenefitId}
            beneficiaries={beneficiariesFiltered}
            primaryTotalAllocation={benefitData?.primaryTotalAllocation}
            secondaryTotalAllocation={benefitData?.secondaryTotalAllocation}
            totalAllocation={totalAllocation}
            zeroAllocation={zeroAllocation}
            emptyAllocation={emptyAllocation}
            isPrimary={isPrimary}
            setDisabled={setDisabled}
            emptyPrimaryBeneficiaries={emptyPrimaryBeneficiaries}
            coverageFields={coverageFields}
            autoSave={autoSave}
            numCoverageLines={numCoverageLines}
            allBeneficiariesPerBenefit={beneficiaries}
          />
        </div>
      </div>
    </>
  );
};
